/**
 * Application layout shell — header, sub-header, bottom panels, floating overlays.
 * Pure DOM construction functions. Called by main.ts boot().
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { DESK_PRESETS, getDeskById } from '@/config/desk-presets';
import { panelManager } from '@/panels/panel-manager';
import { createThreatTempo } from '@/ui/components/threat-tempo';
import { createIntelBadge } from '@/ui/components/intel-badge';
import { createSignalIndicator } from '@/ui/components/signal-indicator';
import { createUpdateBadge } from '@/ui/components/update-badge';
import { showExportDialog } from '@/ui/components/export-dialog';
import type { TimeRange } from '@/core/store';

import { createPanelPicker } from '@/ui/components/panel-picker';
import { persistDesk } from './services';

/** Declared by Vite define config. */
declare const __APP_VERSION__: string;

function createDivider(): HTMLElement {
  const d = document.createElement('div');
  d.className = 'header-divider';
  return d;
}

/** Clean up export resources. Called during teardown (no-op since export dialog is self-cleaning). */
export function cleanupExportDropdown(): void {
  // Export dialog is a one-shot modal — no persistent listeners to clean up
}

/** Show a one-time toast notification for secret migration. */
export function showMigrationToast(): void {
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed; bottom: 48px; left: 50%; transform: translateX(-50%);
    background: var(--bg-secondary, #161616); color: var(--text-primary, #e0e0e0);
    padding: 10px 20px; border-radius: 8px; font-size: 13px; z-index: 9999;
    border: 1px solid var(--border-primary, #2a2a2a); box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    transition: opacity 0.3s ease;
  `;
  toast.textContent = 'API keys migrated to secure storage';
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 4000);
}

/** Switch to a different desk preset and update UI. */
export function switchVariant(deskId: string): void {
  const current = appStore.getState().variant;
  if (current === deskId) return;

  const desk = getDeskById(deskId);
  if (!desk) return;

  appStore.setState({
    variant: deskId,
    activeLayers: desk.defaultLayers,
  });
  eventBus.emit('variant:changed', { from: current, to: deskId });

  // Update desk selector UI
  const buttons = document.querySelectorAll('.desk-btn');
  buttons.forEach(btn => {
    const el = btn as HTMLElement;
    el.classList.toggle('active', el.dataset.desk === deskId);
  });

  // Update desk name display
  const nameEl = document.getElementById('desk-name');
  if (nameEl) nameEl.textContent = desk.name;

  // Persist selection
  try { localStorage.setItem('sentinel-desk', deskId); } catch { /* ignore */ }
}

/** Set the active time range and update UI. */
function setTimeRange(range: TimeRange): void {
  const current = appStore.getState().timeRange;
  if (current === range) return;

  appStore.setState({ timeRange: range });

  const buttons = document.querySelectorAll('.time-range-pill');
  buttons.forEach(btn => {
    const el = btn as HTMLElement;
    const isActive = el.dataset.range === range;
    el.classList.toggle('active', isActive);
    el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

/** Build the export button (opens export dialog). */
function buildExportButton(): HTMLElement {
  const btn = document.createElement('button');
  btn.className = 'header-btn';
  btn.title = 'Export data (Ctrl+E)';
  btn.setAttribute('aria-label', 'Export data');
  btn.textContent = '\u21E9';
  btn.onclick = () => showExportDialog();
  return btn;
}

/** Build the header bar element. */
export function buildHeader(): HTMLElement {
  const header = document.createElement('div');
  header.id = 'header-bar';

  // Logo
  const logo = document.createElement('span');
  logo.className = 'header-logo';
  logo.textContent = 'Sentinel';
  header.appendChild(logo);

  // Version
  const version = document.createElement('span');
  version.className = 'header-version';
  version.textContent = `v${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.1.0'}`;
  header.appendChild(version);

  header.appendChild(createDivider());

  // Threat Tempo indicator
  const tempo = createThreatTempo();
  header.appendChild(tempo);

  header.appendChild(createDivider());

  // Desk preset selector
  const deskSelector = document.createElement('div');
  deskSelector.className = 'desk-selector';

  const currentDesk = getDeskById(appStore.getState().variant);
  const deskName = document.createElement('span');
  deskName.className = 'desk-name';
  deskName.id = 'desk-name';
  deskName.textContent = currentDesk?.name ?? 'General OSINT';
  deskSelector.appendChild(deskName);

  for (const preset of DESK_PRESETS) {
    const btn = document.createElement('button');
    btn.className = 'desk-btn';
    btn.dataset.desk = preset.id;
    btn.innerHTML = preset.icon;
    btn.title = `${preset.name} — ${preset.description}`;
    if (preset.id === appStore.getState().variant) {
      btn.classList.add('active');
    }
    btn.onclick = () => switchVariant(preset.id);
    deskSelector.appendChild(btn);
  }
  header.appendChild(deskSelector);

  header.appendChild(createDivider());

  // Search trigger
  const searchTrigger = document.createElement('button');
  searchTrigger.className = 'header-search-trigger';
  searchTrigger.setAttribute('aria-label', 'Search (Ctrl+K)');
  searchTrigger.onclick = () => {
    eventBus.emit('panel:toggled', { panelId: 'search', visible: true });
  };

  const searchIcon = document.createElement('span');
  searchIcon.className = 'header-search-icon';
  searchIcon.innerHTML = `<svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5"/>
    <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`;
  searchTrigger.appendChild(searchIcon);

  const searchText = document.createElement('span');
  searchText.className = 'header-search-text';
  searchText.textContent = 'Search...';
  searchTrigger.appendChild(searchText);

  const searchKbd = document.createElement('kbd');
  searchKbd.className = 'header-search-kbd';
  searchKbd.textContent = navigator.platform.includes('Mac') ? '\u2318K' : 'Ctrl+K';
  searchTrigger.appendChild(searchKbd);

  header.appendChild(searchTrigger);

  // Spacer
  const spacer = document.createElement('div');
  spacer.className = 'header-spacer';
  header.appendChild(spacer);

  // Intel findings badge
  const intelBadge = createIntelBadge();
  header.appendChild(intelBadge);

  // Signal strength indicator
  const signal = createSignalIndicator();
  header.appendChild(signal);

  header.appendChild(createDivider());

  // Export button
  const exportBtn = buildExportButton();
  header.appendChild(exportBtn);

  header.appendChild(createDivider());

  // Update badge (desktop only)
  const updateBadge = createUpdateBadge();
  header.appendChild(updateBadge);

  // Settings button
  const settingsBtn = document.createElement('button');
  settingsBtn.className = 'header-btn';
  settingsBtn.id = 'settings-btn';
  settingsBtn.title = 'Settings (Ctrl+,)';
  settingsBtn.setAttribute('aria-label', 'Settings');
  settingsBtn.textContent = '\u2699';
  settingsBtn.onclick = () => panelManager.togglePanel('settings');
  header.appendChild(settingsBtn);

  return header;
}

/** Build the sub-header bar: title, status indicators, UTC clock, toggles. */
export function buildSubHeader(): HTMLElement {
  const bar = document.createElement('div');
  bar.id = 'sub-header-bar';

  // Title
  const title = document.createElement('span');
  title.className = 'sub-header-title';
  title.id = 'sub-header-title';
  title.textContent = 'GLOBAL SITUATION';
  bar.appendChild(title);

  // Separator
  const sep1 = document.createElement('div');
  sep1.className = 'sub-header-sep';
  bar.appendChild(sep1);

  // Status cluster
  const status = document.createElement('div');
  status.className = 'sub-header-status';

  const dot = document.createElement('div');
  dot.className = 'status-dot online';
  dot.id = 'status-dot';
  dot.title = 'Online';
  status.appendChild(dot);

  const eventCount = document.createElement('span');
  eventCount.id = 'event-count';
  eventCount.textContent = 'Events: 0';
  status.appendChild(eventCount);

  const midDot2 = document.createElement('span');
  midDot2.textContent = '\u00B7';
  midDot2.style.color = 'var(--text-muted)';
  status.appendChild(midDot2);

  const sourceStatus = document.createElement('span');
  sourceStatus.id = 'source-status';
  sourceStatus.textContent = 'Sources: 0';
  status.appendChild(sourceStatus);

  bar.appendChild(status);

  // Spacer
  const spacer = document.createElement('div');
  spacer.className = 'sub-header-spacer';
  bar.appendChild(spacer);

  // UTC clock
  const clock = document.createElement('div');
  clock.className = 'utc-clock';
  clock.id = 'utc-clock';
  bar.appendChild(clock);

  // Bottom panels toggle
  const toggleBtn = document.createElement('button');
  toggleBtn.className = 'sub-header-btn';
  toggleBtn.id = 'bottom-toggle-btn';
  toggleBtn.title = 'Toggle bottom panels';
  toggleBtn.textContent = '\u25BE';
  toggleBtn.onclick = () => {
    const grid = document.getElementById('bottom-panel-grid');
    if (grid) {
      grid.classList.toggle('collapsed');
      toggleBtn.textContent = grid.classList.contains('collapsed') ? '\u25B8' : '\u25BE';
    }
  };
  bar.appendChild(toggleBtn);

  // Fullscreen button
  const fsBtn = document.createElement('button');
  fsBtn.className = 'sub-header-btn';
  fsBtn.id = 'fullscreen-btn';
  fsBtn.title = 'Toggle fullscreen';
  fsBtn.textContent = '\u26F6';
  fsBtn.onclick = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };
  bar.appendChild(fsBtn);

  return bar;
}

/** Build the floating time range pill overlay. */
function buildTimeRangeOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.id = 'time-range-overlay';
  overlay.className = 'time-range-overlay';

  const ranges: { id: TimeRange; label: string }[] = [
    { id: '1h', label: '1h' },
    { id: '6h', label: '6h' },
    { id: '24h', label: '24h' },
    { id: '48h', label: '48h' },
    { id: '7d', label: '7d' },
    { id: 'all', label: 'All' },
  ];

  const currentRange = appStore.getState().timeRange;
  for (const range of ranges) {
    const btn = document.createElement('button');
    btn.className = 'time-range-pill';
    btn.dataset.range = range.id;
    btn.textContent = range.label;
    btn.title = range.id === 'all' ? 'Show all events' : `Show events from last ${range.label}`;
    btn.setAttribute('aria-pressed', range.id === currentRange ? 'true' : 'false');
    if (range.id === currentRange) {
      btn.classList.add('active');
    }
    btn.onclick = () => setTimeRange(range.id);
    overlay.appendChild(btn);
  }

  return overlay;
}

/** Build the floating layers overlay for the top-left of the map. */
export function buildFloatingLayers(): HTMLElement {
  const container = document.createElement('div');
  container.id = 'floating-layers';

  const header = document.createElement('div');
  header.className = 'floating-layers-header';

  const title = document.createElement('span');
  title.className = 'floating-layers-title';
  title.textContent = 'LAYERS';
  header.appendChild(title);

  const toggle = document.createElement('span');
  toggle.className = 'floating-layers-toggle';
  toggle.textContent = '\u25BE';
  header.appendChild(toggle);

  header.onclick = () => {
    container.classList.toggle('collapsed');
    toggle.textContent = container.classList.contains('collapsed') ? '\u25B8' : '\u25BE';
  };

  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'floating-layers-body';
  container.appendChild(body);

  return container;
}

/** Build the bottom panel grid — split layout: situation (left) | investigations (right). */
export function buildBottomPanelGrid(): HTMLElement {
  const grid = document.createElement('div');
  grid.id = 'bottom-panel-grid';

  // ── Resize handle (WP-10.3) ──
  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'bottom-panel-resize-handle';
  grid.appendChild(resizeHandle);
  initBottomResize(resizeHandle, grid);

  // Restore persisted height
  const savedHeight = loadBottomHeight();
  if (savedHeight) grid.style.height = `${savedHeight}px`;

  // Full-width wrapper
  const wrapper = document.createElement('div');
  wrapper.id = 'bottom-panel-left';
  wrapper.className = 'bottom-panel';
  wrapper.style.cssText = 'display: flex; flex-direction: column; height: 100%;';

  // ── Body: split layout (each column has its own header) ──
  const body = document.createElement('div');
  body.className = 'bottom-panel-body';
  body.style.cssText = 'display: flex; flex: 1; overflow: hidden;';

  // ── LEFT COLUMN: Tabbed (Situation / News / Sources) — 50% ──
  const leftCol = document.createElement('div');
  leftCol.style.cssText = 'width: 50%; display: flex; flex-direction: column; border-right: 1px solid var(--border-primary, #1a1f2e); overflow: hidden;';

  // Left column tab bar
  const leftHeader = document.createElement('div');
  leftHeader.className = 'bottom-tab-bar';
  leftHeader.style.cssText = 'display: flex; align-items: center; flex-shrink: 0;';

  // Tab definitions for left column
  const leftTabs: { id: string; label: string; badgeId?: string }[] = [
    { id: 'situation', label: 'SITUATION', badgeId: 'situation-badge' },
    { id: 'news', label: 'NEWS' },
  ];

  function switchLeftTab(tabId: string): void {
    // Toggle tab buttons
    leftHeader.querySelectorAll('.bottom-tab').forEach(t => {
      const el = t as HTMLElement;
      el.classList.toggle('active', el.dataset.tab === tabId);
    });
    // Toggle pages
    leftCol.querySelectorAll('.bottom-tab-page').forEach(p => {
      const el = p as HTMLElement;
      const isActive = el.dataset.tabPage === tabId;
      el.classList.toggle('active', isActive);
      el.style.display = isActive ? '' : 'none';
    });
  }

  for (const tab of leftTabs) {
    const btn = document.createElement('span');
    btn.className = 'bottom-tab' + (tab.id === 'situation' ? ' active' : '');
    btn.dataset.tab = tab.id;
    btn.style.cssText = 'font-size: 9px; font-weight: 600; letter-spacing: 0.5px; padding: 5px 12px; cursor: pointer;';
    btn.textContent = tab.label;

    if (tab.badgeId) {
      const badge = document.createElement('span');
      badge.className = 'tab-badge';
      badge.id = tab.badgeId;
      badge.style.display = 'none';
      btn.appendChild(badge);
    }

    btn.addEventListener('click', () => switchLeftTab(tab.id));
    leftHeader.appendChild(btn);
  }

  // Spacer
  const leftSpacer = document.createElement('div');
  leftSpacer.style.cssText = 'flex: 1;';
  leftHeader.appendChild(leftSpacer);

  // Time range pills
  const timeRangeWrap = document.createElement('div');
  timeRangeWrap.className = 'bottom-tab-time-pills';
  const timeRangeOverlay = buildTimeRangeOverlay();
  while (timeRangeOverlay.firstChild) {
    timeRangeWrap.appendChild(timeRangeOverlay.firstChild);
  }
  leftHeader.appendChild(timeRangeWrap);

  // Panel Picker gear icon (WP-10.1)
  const { element: pickerEl } = createPanelPicker();
  leftHeader.appendChild(pickerEl);

  leftCol.appendChild(leftHeader);

  // Tab pages — only the active one is visible
  const sitPage = document.createElement('div');
  sitPage.className = 'bottom-tab-page active';
  sitPage.dataset.tabPage = 'situation';
  sitPage.style.cssText = 'flex: 1; overflow-y: auto;';
  leftCol.appendChild(sitPage);

  const newsPage = document.createElement('div');
  newsPage.className = 'bottom-tab-page';
  newsPage.dataset.tabPage = 'news';
  newsPage.style.cssText = 'flex: 1; overflow-y: auto; display: none;';
  leftCol.appendChild(newsPage);

  body.appendChild(leftCol);

  // ── RIGHT COLUMN: AI Investigations (flex 1) ──
  const rightCol = document.createElement('div');
  rightCol.style.cssText = 'flex: 1; display: flex; flex-direction: column; overflow: hidden;';

  // Right column header
  const rightHeader = document.createElement('div');
  rightHeader.className = 'bottom-tab-bar';
  rightHeader.style.cssText = 'display: flex; align-items: center; flex-shrink: 0;';

  const invLabel = document.createElement('span');
  invLabel.style.cssText = 'font-size: 9px; font-weight: 600; letter-spacing: 0.5px; color: #00b4d8; padding: 5px 12px;';
  invLabel.textContent = 'AI INVESTIGATIONS';
  rightHeader.appendChild(invLabel);

  const invPulse = document.createElement('div');
  invPulse.id = 'inv-header-pulse';
  invPulse.style.cssText = `
    width: 5px; height: 5px; border-radius: 50%;
    background: #00b4d8; margin-left: 2px; display: none;
  `;
  rightHeader.appendChild(invPulse);

  rightCol.appendChild(rightHeader);

  // Investigation panel mount (the investigation-panel.ts component mounts here)
  const invMount = document.createElement('div');
  invMount.id = 'bottom-investigation-mount';
  invMount.style.cssText = 'flex: 1; display: flex; flex-direction: column; overflow: hidden;';
  rightCol.appendChild(invMount);

  body.appendChild(rightCol);

  wrapper.appendChild(body);
  grid.appendChild(wrapper);

  return grid;
}

// ── WP-10.3: Bottom panel resize ──

function getBottomHeightKey(): string {
  return `sentinel-bottom-height-${appStore.getState().variant}`;
}

function loadBottomHeight(): number | null {
  try {
    const raw = localStorage.getItem(getBottomHeightKey());
    if (!raw) return null;
    const val = parseInt(raw, 10);
    return Number.isFinite(val) ? val : null;
  } catch { return null; }
}

function saveBottomHeight(height: number): void {
  try {
    localStorage.setItem(getBottomHeightKey(), String(Math.round(height)));
  } catch { /* ignore */ }
}

function initBottomResize(handle: HTMLElement, grid: HTMLElement): void {
  let startY = 0;
  let startHeight = 0;
  let isResizing = false;

  function onMouseDown(e: MouseEvent): void {
    e.preventDefault();
    isResizing = true;
    startY = e.clientY;
    startHeight = grid.getBoundingClientRect().height;
    handle.classList.add('active');
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }

  function onMouseMove(e: MouseEvent): void {
    if (!isResizing) return;
    // Dragging up increases height (startY > e.clientY)
    const delta = startY - e.clientY;
    const newHeight = Math.max(120, Math.min(startHeight + delta, window.innerHeight * 0.6));
    grid.style.height = `${newHeight}px`;
  }

  function onMouseUp(): void {
    if (!isResizing) return;
    isResizing = false;
    handle.classList.remove('active');
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // Persist
    const finalHeight = grid.getBoundingClientRect().height;
    saveBottomHeight(finalHeight);
  }

  handle.addEventListener('mousedown', onMouseDown);
}

/**
 * Show a first-launch desk preset selector modal.
 * Returns a promise that resolves with the selected desk ID.
 * The modal blocks interaction with the app until a choice is made.
 */
export function showDeskSelectorModal(): Promise<string> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'desk-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'desk-modal';

    const title = document.createElement('h2');
    title.className = 'desk-modal-title';
    title.textContent = 'Choose your focus';
    modal.appendChild(title);

    const subtitle = document.createElement('p');
    subtitle.className = 'desk-modal-subtitle';
    subtitle.textContent = 'Select a desk preset to configure your layers, feeds, and panels. You can switch anytime.';
    modal.appendChild(subtitle);

    const grid = document.createElement('div');
    grid.className = 'desk-modal-grid';

    for (const preset of DESK_PRESETS) {
      const card = document.createElement('button');
      card.className = 'desk-modal-card';
      card.dataset.desk = preset.id;

      const icon = document.createElement('span');
      icon.className = 'desk-modal-card-icon';
      icon.innerHTML = preset.icon;
      card.appendChild(icon);

      const name = document.createElement('span');
      name.className = 'desk-modal-card-name';
      name.textContent = preset.name;
      card.appendChild(name);

      const desc = document.createElement('span');
      desc.className = 'desk-modal-card-desc';
      desc.textContent = preset.description;
      card.appendChild(desc);

      card.onclick = () => {
        const deskId = preset.id;
        persistDesk(deskId);
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 200);
        resolve(deskId);
      };

      grid.appendChild(card);
    }

    modal.appendChild(grid);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Trigger entrance animation
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
    });
  });
}
